/**
 * Audio processing utilities for encoding and streaming audio to the AWS Transcribe WebSocket API.
 *
 * @module utils/convertIntoBuffer
 * @author Snowfly
 * @version 1.0.0
 */
// reference https://github.com/amazon-archives/amazon-transcribe-websocket-static
import {EventStreamCodec} from "@aws-sdk/eventstream-codec";
const util_utf8_node = require("@aws-sdk/util-utf8-node"); // utilities for encoding and decoding UTF8
/**
 * AWS event stream codec instance for encoding and decoding WebSocket messages
 * sent to and received from the AWS Transcribe streaming API.
 *
 * @type {EventStreamCodec}
 */
const eventStreamMarshaller = new EventStreamCodec(
    util_utf8_node.toUtf8,
    util_utf8_node.fromUtf8
);

/**
 * Accumulates partial transcript text between AWS Transcribe response chunks.
 * Must be reset with {@link resetEventStreamMessage} before each new recording session.
 *
 * @type {string}
 */
let partialTranscription = '';

/**
 * Encodes a given Float32Array audio input into a 16-bit PCM buffer.
 *
 * @param {Float32Array} input - The array of floating-point audio data ranging between -1.0 and 1.0.
 * @returns {ArrayBuffer} The encoded PCM data as a 16-bit little-endian ArrayBuffer.
 */
export const pcmEncode = (input) => {
    let offset = 0,
        buffer = new ArrayBuffer(input.length * 2),
        view = new DataView(buffer);

    for (let i = 0; i < input.length; i++, offset += 2) {
        let s = Math.max(-1, Math.min(1, input[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }

    return buffer;
};

/**
 * Downsamples an audio buffer from a higher sample rate to a lower sample rate.
 *
 * This function takes a Float32Array representing an audio buffer, the input sample rate,
 * and the desired output sample rate. It returns a new Float32Array that is downsampled
 * to the specified output sample rate. The function uses linear averaging to reduce the sample rate.
 *
 * @param {Float32Array} buffer - The input audio buffer to be downsampled.
 * @param {number} [inputSampleRate=44100] - The sample rate of the input audio buffer (default is 44,100 Hz).
 * @param {number} [outputSampleRate=16000] - The desired sample rate for the output (default is 16,000 Hz).
 * @returns {Float32Array} The downsampled audio buffer at the desired sample rate.
 */
export const downsampleBuffer = (
    buffer,
    inputSampleRate = 44100,
    outputSampleRate = 16000
) => {
    if (outputSampleRate === inputSampleRate) {
        return buffer;
    }

    let sampleRateRatio = inputSampleRate / outputSampleRate,
        newLength = Math.round(buffer.length / sampleRateRatio),
        result = new Float32Array(newLength),
        offsetResult = 0,
        offsetBuffer = 0;

    while (offsetResult < result.length) {
        let nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio),
            accum = 0,
            count = 0;

        for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
            accum += buffer[i];
            count++;
        }

        result[offsetResult] = accum / count;
        offsetResult++;
        offsetBuffer = nextOffsetBuffer;
    }

    return result;
};

/**
 * Wraps a raw audio buffer in a WebSocket event message envelope for the AWS Transcribe streaming API.
 *
 * @param {ArrayBuffer} buffer - The raw PCM audio data buffer to wrap.
 * @returns {{headers: Object, body: ArrayBuffer}} An event message object with AWS event stream headers and the audio body.
 */
export const getAudioEventMessage = (buffer) => {
    // wrap the audio data in a JSON envelope
    return {
        headers: {
            ":message-type": {
                type: "string",
                value: "event",
            },
            ":event-type": {
                type: "string",
                value: "AudioEvent",
            },
        },
        body: buffer,
    };
};

/**
 * Resets the partialTranscription variable used by handleEventStreamMessage to
 * keep track of the partial conversion of the speech-to-text engine.
 *
 * This has to be called either BEFORE starting a new audio recording to be
 * sent to the speech-to-text engine or
 * AFTER the conversion has been completed and the transcript has been
 * accepted/used.
 */
export const resetEventStreamMessage = () => {
    partialTranscription = '';
};

/**
 * Processes a transcript message from the AWS Transcribe event stream.
 *
 * Accumulates partial results in a module-level variable and builds the final
 * transcription string once the engine marks a result as non-partial. Returns
 * both the final and partial strings so callers can fall back to the partial
 * when the final is empty (e.g. due to network errors or low audio levels).
 *
 * @param {Object} messageJson - The parsed JSON message received from the AWS Transcribe event stream.
 * @param {string} transcription - The accumulated final transcription string built so far.
 * @returns {[string, string, boolean]} A tuple of [finalTranscription, partialTranscription, isFinal].
 */
export const handleEventStreamMessage = (messageJson, transcription) => {
    let results = messageJson.Transcript.Results,
        isFinal = false;
    //console.log('WebSocket Transcript results = %o', results);

    if (results.length > 0) {
        if (results[0].Alternatives.length > 0) {
            if (results[0].IsPartial) {
                partialTranscription = '';
            } else {
                isFinal = true;
                partialTranscription = transcription;
            }

            let transcript = results[0].Alternatives[0].Transcript;

            // fix encoding for accented characters
            transcript = decodeURIComponent(encodeURIComponent(transcript));

            if (!results[0].IsPartial) {
                transcription += transcript + " ";
            } else {
                partialTranscription += transcript + " "
            }
        }
    }

    return [transcription, partialTranscription, isFinal];
};

/**
 * Converts a raw microphone audio chunk into a binary message for the AWS Transcribe WebSocket API.
 *
 * Downsamples the raw audio, encodes it as 16-bit PCM, wraps it in an AWS event
 * stream envelope, and marshals it to a Uint8Array ready for transmission.
 *
 * @param {Blob} audioChunk - The raw audio data blob from the microphone stream.
 * @returns {Uint8Array|null} The encoded binary message ready for WebSocket transmission, or null if the chunk is empty.
 */
export const convertAudioToBinaryMessage = (audioChunk) => {
    const sRate = 44100;
    let raw = new Float32Array(audioChunk.buffer), //MicrophoneStream.toRaw(audioChunk),
        result = null;

    if (raw !== null) {
        let audioBuff = downsampleBuffer(raw, sRate, sRate),
            encoded = pcmEncode(audioBuff),
            evMsg = getAudioEventMessage(Buffer.from(encoded));
        result = eventStreamMarshaller.encode(evMsg);
    }

    return result;
};
